/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import {
  Table,
  Modal,
  Form,
  InputNumber,
  Button,
  message,
  Card,
  Space,
  Typography,
  Input,
} from "antd";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { apiClient, getStorageUrl } from "../../../shared/api/AllApi";
import { invalidateCatalog, useProductsCatalog } from "../../../hooks/useCatalogData";
import { parseLocalizedName } from "../../../utils/localization";
import usePermissions from "../../../hooks/usePermissions";
import { filterBySearch } from "../../../shared/lib/listSearch";
import ListSearch from "../../../shared/ui/ListSearch";
import "../../../shared/styles/Stock.css";
import "../../../shared/styles/ListPages.css";

const { TextArea } = Input;
const { Text } = Typography;

const Stock = () => {
  const { hasPermission } = usePermissions();
  const [allProducts, setAllProducts] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);

  const { i18n, t } = useTranslation();
  const { data: catalogProducts, loading } = useProductsCatalog();

  useEffect(() => {
    const formattedProducts = catalogProducts.map((item) => ({
      id: item.id,
      name: parseLocalizedName(item.name, i18n.language, "N/A"),
      image_url: item.image_url ? getStorageUrl(item.image_url) : null,
      price: item.price,
      sku: item.sku || "",
      stock: item.stock,
    }));
    setAllProducts(formattedProducts);
  }, [catalogProducts, i18n.language]);

  const filteredProducts = useMemo(
    () =>
      filterBySearch(allProducts, searchText, (item) => [
        item.id,
        item.sku,
        item.name,
        item.price,
        item.stock,
      ]),
    [allProducts, searchText]
  );

  const addStock = async (id, quantity, reason) => {
    try {
      const response = await apiClient.post(`/products/${id}/add-stock`, {
        quantity,
        ...(reason && String(reason).trim() ? { reason: String(reason).trim() } : {}),
      });

      if (response.status === 200) {
        invalidateCatalog("products");
        message.success(t("stockAdjustSuccess"));
        const serverStock = response.data?.product?.stock;
        setAllProducts((prev) =>
          prev.map((p) => {
            if (p.id !== id) return p;
            const stock =
              serverStock !== undefined ? Number(serverStock) : p.stock + quantity;
            return { ...p, stock };
          })
        );
        setSelectedProduct(null);
      }
    } catch (error) {
      message.error(t("stockAdjustError"));
      console.error("Error adding stock:", error);
    }
  };

  const removeStock = async (id, quantity, reason) => {
    try {
      const response = await apiClient.post(`/products/${id}/remove-stock`, {
        quantity,
        ...(reason && String(reason).trim() ? { reason: String(reason).trim() } : {}),
      });

      if (response.status === 200) {
        invalidateCatalog("products");
        message.success(t("stockAdjustSuccess"));
        const serverStock = response.data?.product?.stock;
        setAllProducts((prev) =>
          prev.map((p) => {
            if (p.id !== id) return p;
            const stock =
              serverStock !== undefined ? Number(serverStock) : p.stock - quantity;
            return { ...p, stock };
          })
        );
        setSelectedProduct(null);
      }
    } catch (error) {
      message.error(error?.response?.data?.error || t("stockAdjustError"));
      console.error("Error removing stock:", error);
    }
  };

  const ModalContent = () => {
    const [inputValue, setInputValue] = useState(0);
    const [reason, setReason] = useState("");

    useEffect(() => {
      setInputValue(0);
      setReason("");
    }, [selectedProduct?.id]);

    if (!selectedProduct) return null;

    const handleIncrement = () => {
      addStock(selectedProduct.id, inputValue, reason);
    };

    const handleDecrement = () => {
      removeStock(selectedProduct.id, inputValue, reason);
    };

    return (
      <Modal
        open={!!selectedProduct}
        onCancel={() => setSelectedProduct(null)}
        footer={null}
        width={440}
        title={t("stockAdjustTitle")}
      >
        <div style={{ textAlign: "center" }}>
          <Text strong style={{ fontSize: 16 }}>
            {selectedProduct.name}
          </Text>
          <p>
            <Text type="secondary">{t("stock")}: </Text>
            <Text>{selectedProduct.stock}</Text>
          </p>
          <Form layout="vertical" style={{ textAlign: "left", marginTop: 12 }}>
            <Form.Item label={t("quantity")}>
              <InputNumber
                min={0}
                style={{ width: "100%" }}
                value={inputValue}
                onChange={(value) => setInputValue(value || 0)}
              />
            </Form.Item>
            <Form.Item label={t("adjustmentReasonOptional")}>
              <TextArea
                rows={2}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={t("adjustmentReasonPlaceholder")}
              />
            </Form.Item>
            {hasPermission("stock.add") ? (
              <Form.Item>
                <Space>
                  <Button
                    type="primary"
                    onClick={handleIncrement}
                    disabled={inputValue <= 0}
                  >
                    + {t("stockIn")}
                  </Button>
                  <Button
                    type="primary"
                    danger
                    onClick={handleDecrement}
                    disabled={
                      inputValue <= 0 || selectedProduct.stock - inputValue < 0
                    }
                  >
                    − {t("stockOut")}
                  </Button>
                </Space>
              </Form.Item>
            ) : (
              <Text type="secondary">{t("noPermission")}</Text>
            )}
          </Form>
        </div>
      </Modal>
    );
  };

  const columns = [
    {
      title: t("Image"),
      dataIndex: "image_url",
      key: "img",
      width: 88,
      render: (url) =>
        url ? (
          <img src={url} alt="" height={56} width={56} style={{ objectFit: "cover", borderRadius: 4 }} />
        ) : (
          <div
            style={{
              width: 56,
              height: 56,
              background: "#f0f0f0",
              borderRadius: 4,
            }}
          />
        ),
    },
    {
      title: t("name"),
      dataIndex: "name",
      key: "name",
      render: (name, row) => (
        <Link to={`/products/${row.id}/history`}>{name}</Link>
      ),
    },
    {
      title: t("sku"),
      dataIndex: "sku",
      key: "sku",
      width: 120,
    },
    {
      title: t("stock"),
      dataIndex: "stock",
      key: "stock",
      width: 100,
    },
    {
      title: t("actions"),
      key: "actions",
      width: 220,
      render: (_, row) => (
        <Space wrap>
          <Link to={`/products/${row.id}/history`}>{t("stockMovesLink")}</Link>
          <Button type="link" size="small" onClick={() => setSelectedProduct(row)}>
            {t("stockAdjustButton")}
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div className="list-page">
      <Card
        title={
          <span style={{ fontWeight: "600", fontSize: "1.125rem" }}>
            {t("stock")}
          </span>
        }
        variant="borderless"
      >
        <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
          {t("stockPageSubtitle")}
        </Text>
        <ListSearch
          value={searchText}
          onChange={setSearchText}
          width={360}
          style={{ marginBottom: 16 }}
        />

        <Table
          rowKey="id"
          loading={loading}
          dataSource={filteredProducts}
          columns={columns}
          pagination={{ pageSize: 15, showSizeChanger: true }}
          scroll={{ x: 720 }}
        />

        <ModalContent />
      </Card>
    </div>
  );
};

export default Stock;
